import { useProcoreOAuthExchangeMutation } from 'apollo/generated/client-operations';
import React, { ReactElement, useRef } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

/**
 * Lands after Procore redirects the browser back from the OAuth dance. Reads
 * `code` + `state` from the URL, calls the server's exchange mutation (which
 * validates state, swaps the code for tokens, captures the Procore user id,
 * and writes the FA IdP link), then redirects back to the settings page with
 * a success/failure marker.
 */
export function ProcoreAuthTokenExchangeContainer(): ReactElement {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const code = params.get('code');
  const state = params.get('state');

  const [exchange, { data, error, loading, called }] =
    useProcoreOAuthExchangeMutation();

  // Authorization codes are single-use. Guard against double-firing a second
  // exchange and triggering a spurious failure.
  const exchangeAttemptedRef = useRef('');
  React.useEffect(() => {
    if (!code || !state) return;
    if (exchangeAttemptedRef.current === code + state) return;
    exchangeAttemptedRef.current = code + state;
    exchange({ variables: { code, state } }).catch(() => {});
  }, [code, state, exchange]);

  const redirect = (success: boolean): ReactElement => (
    <Redirect
      push
      to={`/settings/external-connections?linkedService=Procore&${
        success ? 'succeeded' : 'failed'
      }`}
    />
  );

  // Missing query params — fail fast.
  if (!code || !state) return redirect(false);

  if (loading || !called) return null;

  if (data?.procoreOAuthExchange.connected) return redirect(true);
  if (error || (data && !data.procoreOAuthExchange.connected))
    return redirect(false);

  return null;
}
